import { type FunctionComponent, type ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { assert } from 'ts-essentials';
import { authSlice } from '../../core/auth/auth.slice';
import { type StreamState } from '../../core/stream/stream.slice';
import { SplashPage } from '../../pages/SplashPage/SplashPage';
import { AccessList, AppRoute } from '../../routes';
import { getAppRoute } from '../../utils/protected-routes';
import {
  getLoginRequired,
  RouteAccess,
  verifyRouteAccess,
} from '../../utils/route-access';

export type ProtectedRouteProps = {
  readonly children?: ReactNode;
  readonly dataLoaded: boolean;
  readonly loggedIn?: boolean;
  readonly stream: StreamState;
};

export const ProtectedRoute: FunctionComponent<ProtectedRouteProps> = ({
  children,
  dataLoaded,
  loggedIn = false,
  stream,
}): ReactNode => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const route = getAppRoute(pathname);
  assert(route);
  const routeAccess = AccessList[route];
  const accessAllowed = verifyRouteAccess(routeAccess, loggedIn, stream);
  const loginRequired = getLoginRequired(routeAccess);

  useEffect(() => {
    if (!loggedIn && loginRequired) {
      if (pathname !== AppRoute['/']) {
        dispatch(
          authSlice.actions.setContinueRoutePostLogin(`${pathname}${search}`),
        );
      }
      void navigate(AppRoute['/login']);
    }
  }, [dispatch, loggedIn, loginRequired, navigate, pathname, search]);

  if (routeAccess.includes(RouteAccess.EVERYONE)) {
    return children;
  }

  if (routeAccess.includes(RouteAccess.GUEST) && !loggedIn) {
    return children;
  }

  if (!loggedIn && loginRequired) {
    return null;
  }

  if (
    !dataLoaded &&
    pathname !== AppRoute['/login'] &&
    pathname !== AppRoute['/logout']
  ) {
    return <SplashPage />;
  }

  if (!accessAllowed) {
    return <Navigate to={AppRoute['/']} />;
  }

  return children;
};
